import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take } from 'rxjs/operators';
import { BaseComponent } from 'src/app/moov-common/base/base.component';
import { UserCompetenciesData } from 'src/app/moov-common/services/chat/strcutredProcess-data';

// Saved results in the shape the competencies app sends in competencies:completed
export interface SavedCompetenciesForApp {
  competencies: { id: string, styles: { id: string }[] }[] | null;
  completedAt: string | null;
}

const NO_SAVED_COMPETENCIES: SavedCompetenciesForApp = { competencies: null, completedAt: null };

@Component({
  selector: 'app-structured-process-compentencies-container',
  templateUrl: './structured-process-compentencies-container.component.html',
  styleUrls: ['./structured-process-compentencies-container.component.scss'] // <-- styleUrls (plural)
})
export class StructuredProcessCompentenciesContainerComponent extends BaseComponent {
  @Input() externalUrl: string = '';
  // Saved results are shown only for a completed step; otherwise the app starts fresh
  @Input() stepCompleted: boolean = false;
  @Output() competenciesCompleted = new EventEmitter<UserCompetenciesData>();
  @Output() competenciesReset = new EventEmitter<void>();

  private savedCompetencies$: Observable<SavedCompetenciesForApp> = of(NO_SAVED_COMPETENCIES);


  // The ACTUAL origin of your embedded app:
  // readonly iframeOrigin = 'https://interactive-tale-maker.lovable.app';

  // Safe resource URL the template can bind to
  iframeSrcSafe!: SafeResourceUrl;

  constructor(private sanitizer: DomSanitizer) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();

    console.log('externalUrl is ', this.externalUrl);

    const url = new URL('/?mode=embed', this.externalUrl);

    // Minimal whitelist check (scheme + host)
    if (url.protocol !== 'https:' || url.host !== new URL(this.externalUrl).host) {
      throw new Error('Blocked unexpected iframe URL');
    }

    // Tell Angular this URL is safe for a resource context
    this.iframeSrcSafe = this.sanitizer.bypassSecurityTrustResourceUrl(url.toString());

    // Start loading now, so the results are usually ready by the time the app asks for them
    this.savedCompetencies$ = this.loadSavedCompetencies();
    this.savedCompetencies$.pipe(take(1)).subscribe();

    window.addEventListener('message', this.onMessage);
  }

  loadSavedCompetencies(): Observable<SavedCompetenciesForApp> {
    if (!this.stepCompleted) {
      return of(NO_SAVED_COMPETENCIES);
    }

    return this.personalUserDataServiceForBaseClass.getUserCompetencies().pipe(
      map((saved) => saved?.competencies?.length ? {
        competencies: saved.competencies.map((c) => ({
          id: c.competencyId,
          styles: c.actionStyles.map((styleId) => ({ id: styleId }))
        })),
        completedAt: saved.completedAt ?? null
      } : NO_SAVED_COMPETENCIES),
      // without its saved results the user still gets a working game, starting from the intro
      catchError(() => of(NO_SAVED_COMPETENCIES)),
      shareReplay(1)
    );
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.onMessage);
  }

  onMessage = (event: MessageEvent) => {
    // console.log('onMessage event is ', JSON.stringify(event.data));
    // console.log('onMessage event origin is ', event.origin);

    // Only accept messages from the embedded origin
    // if (event.origin !== this.externalUrl) return;
    const allowedOrigin = new URL(this.externalUrl).origin;
    if (event.origin !== allowedOrigin) return;

    const data = event.data || {};
    switch (data?.type) {
      case 'competencies:ready': {
        // The app shows the saved results it is given (null = intro), so the next user of the browser starts fresh.
        // Reply through event.source: the iframe sits inside @if, so a static @ViewChild of it stays undefined
        const source = event.source as WindowProxy | null;
        this.savedCompetencies$.pipe(take(1)).subscribe((saved) => {
          source?.postMessage(
            { type: 'host:init', payload: { userId: this.user?.id, ...saved } },
            event.origin
          );
        });
        break;
      }
      case 'competencies:reset':
        this.competenciesReset.emit();
        break;
      case 'competencies:progress':
        // optional
        break;
      case 'competencies:completed':
        const results = data.payload;
        const userCompetenciesData = this.buildUserCompetenciesData(this.user!.id, results);
        this.competenciesCompleted.emit(userCompetenciesData);
        // console.log('results', JSON.stringify(results));
        // handle results in FE
        break;
    }
  };

  buildUserCompetenciesData(userId: string, rawData: any): UserCompetenciesData {
    return {
      userId,
      competencies: rawData.competencies.map((c: any) => ({
        competencyId: c.id,
        actionStyles: c.styles.map((s: any) => s.id)
      }))
    };
  }
}
